import React, { useState } from "react";
import { motion } from "framer-motion";
import { Bot, Check, RotateCcw, ShieldCheck } from "lucide-react";
import { AssistantBar, TypingText, ChatBubble, DemoChip } from "@/components/fittin/mcpShared";
import { ASSISTANTS, B2B_QUESTIONS, B2B_CONFIRM } from "@/lib/mcpDemoContent";

// B2B MCP LIVE DEMO (mock — no real LLM).
// Framed as the RETAILER's own assistant embedded in their chatbot. Selecting a
// question first shows a confirm bubble ("use your stored FitKey?"); on confirm it
// streams a detailed, catalog-aware illustrative answer. Copy is in
// src/lib/mcpDemoContent.js.
export default function RetailerMCPDemo() {
  const [assistant, setAssistant] = useState("claude");
  const [messages, setMessages] = useState([]); // {key, role: user|confirm|assistant, text?, qid?, done?}
  const [usedIds, setUsedIds] = useState([]);

  const activeLabel = ASSISTANTS.find((a) => a.key === assistant)?.label || "Assistant";

  const ask = (question) => {
    if (usedIds.includes(question.id)) return;
    setUsedIds((ids) => [...ids, question.id]);
    setMessages((m) => [
      ...m,
      { key: `${question.id}-u`, role: "user", text: question.q, context: question.context },
      { key: `${question.id}-c`, role: "confirm", qid: question.id, done: false },
    ]);
  };

  const confirm = (question) => {
    setMessages((m) => {
      const next = m.map((msg) =>
        msg.role === "confirm" && msg.qid === question.id ? { ...msg, done: true } : msg
      );
      return [...next, { key: `${question.id}-a`, role: "assistant", text: question.a }];
    });
  };

  const reset = () => {
    setMessages([]);
    setUsedIds([]);
  };

  const findQuestion = (qid) => B2B_QUESTIONS.find((q) => q.id === qid);

  return (
    <section
      id="retailer-mcp-demo"
      data-testid="retailer-mcp-demo-section"
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
            <span className="h-px w-8 bg-black/40" />
            Retailer MCP · Live demo
          </div>
          <h2
            data-testid="retailer-mcp-headline"
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] font-medium leading-[1.02]"
          >
            Fit answers inside <span className="text-neutral-400">your own chatbot.</span>
          </h2>
          <p className="mt-5 max-w-lg text-[15.5px] text-neutral-600 leading-relaxed">
            Embed Fittin as an MCP tool in your retail assistant. It asks the shopper
            for consent to use their FitKey, then answers with brand-accurate sizing from
            your catalog — not generic advice. Try a question to see the flow.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-xl border border-black/10 bg-white px-3.5 py-2 text-[12px] text-neutral-600">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            Consent-gated — the assistant always asks before reading a FitKey.
          </div>
        </div>

        {/* Retailer shop-chat window */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          data-testid="retailer-mcp-card"
          className="lg:col-span-7 rounded-3xl border border-black/10 bg-white overflow-hidden"
        >
          <div className="flex items-center gap-2 px-5 h-11 border-b border-black/5 bg-neutral-50">
            <Bot className="h-3.5 w-3.5 text-neutral-500" />
            <div className="text-[11px] text-neutral-500 font-mono">
              Shop assistant · powered by {activeLabel}
            </div>
            <div className="ml-auto">
              <DemoChip testid="retailer-mcp-chip" />
            </div>
          </div>

          <div className="p-5 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <AssistantBar
                assistants={ASSISTANTS}
                active={assistant}
                onChange={setAssistant}
                testidPrefix="retailer-mcp"
              />
              {messages.length > 0 && (
                <button
                  type="button"
                  data-testid="retailer-mcp-reset"
                  onClick={reset}
                  className="inline-flex items-center gap-1.5 text-[12px] text-neutral-500 hover:text-[#0A0A0A] transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reset
                </button>
              )}
            </div>

            <div
              data-testid="retailer-mcp-chat"
              className="mt-4 min-h-[200px] rounded-2xl bg-[#FAFAFA] border border-black/5 p-4 space-y-3"
            >
              {messages.length === 0 ? (
                <div className="h-full min-h-[170px] grid place-items-center text-center">
                  <div className="text-[13px] text-neutral-400 max-w-[260px]">
                    Pick a shopper question below. The assistant will ask to use the
                    stored FitKey before answering.
                  </div>
                </div>
              ) : (
                messages.map((m) => {
                  if (m.role === "confirm") {
                    const q = findQuestion(m.qid);
                    return (
                      <div key={m.key} className="mr-auto max-w-[88%]">
                        <ChatBubble role="assistant" testid={`retailer-mcp-confirm-${m.qid}`}>
                          {B2B_CONFIRM.text}
                          {!m.done && (
                            <button
                              type="button"
                              data-testid={`retailer-mcp-use-fitid-${m.qid}`}
                              onClick={() => confirm(q)}
                              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] text-white text-[12px] px-3.5 py-1.5 hover:bg-neutral-800 transition-all active:scale-[0.98]"
                            >
                              <Check className="h-3.5 w-3.5" />
                              {B2B_CONFIRM.cta}
                            </button>
                          )}
                          {m.done && (
                            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700">
                              <Check className="h-3 w-3" />
                              FitKey shared
                            </div>
                          )}
                        </ChatBubble>
                      </div>
                    );
                  }
                  return (
                    <div key={m.key} className={m.role === "user" ? "ml-auto max-w-[88%]" : ""}>
                      {m.role === "user" && m.context && (
                        <div className="mb-1 text-right text-[10px] uppercase tracking-[0.16em] text-neutral-400">
                          {m.context}
                        </div>
                      )}
                      <ChatBubble role={m.role} testid={`retailer-mcp-bubble-${m.key}`}>
                        {m.role === "assistant" ? <TypingText text={m.text} /> : m.text}
                      </ChatBubble>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">
                Shopper questions
              </div>
              <div className="mt-2 space-y-2">
                {B2B_QUESTIONS.map((question) => {
                  const used = usedIds.includes(question.id);
                  return (
                    <button
                      key={question.id}
                      type="button"
                      disabled={used}
                      data-testid={`retailer-mcp-question-${question.id}`}
                      onClick={() => ask(question)}
                      className={`flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${
                        used
                          ? "border-emerald-200 bg-emerald-50/50"
                          : "border-black/10 bg-white hover:bg-neutral-50"
                      }`}
                    >
                      <span
                        className={`mt-0.5 grid place-items-center h-4 w-4 shrink-0 rounded border ${
                          used ? "bg-emerald-500 border-emerald-500" : "border-black/20 bg-white"
                        }`}
                      >
                        {used && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="min-w-0">
                        <span className={`block text-[12.5px] ${used ? "text-neutral-400" : "text-neutral-700"}`}>
                          {question.q}
                        </span>
                        <span className="block mt-0.5 text-[10.5px] uppercase tracking-[0.14em] text-neutral-400">
                          {question.context}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-neutral-400">
                Illustrative demo — answers are examples, not verified fit facts.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
